import React, { useEffect, useState } from 'react';
import {
  Grid,
  Container,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Table,
  TableRow,
  TableCell,
  TableHead,
  FormControlLabel,
  Switch,
  Button,
  TableBody,
  Chip,
  TableSortLabel
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  getActiveCraft,
  selectCellClients,
  selectPadConfig
} from 'selectors/liveAircraftSelectors';
import {
  linkPadWithAircraft,
  linkPadWithChannel,
  removeAircraftFromPad,
  updateLandingPadActive
} from 'actions/liveAircraftActions';
import GroupIcon from '@mui/icons-material/Group';
import StorefrontIcon from '@mui/icons-material/Storefront';
import {
  selectSelectedCellId,
  selectLandingZones
} from 'selectors/ordersSelector';
import { selectPointOfSales } from '../../selectors/adminSelector';

interface TeamsProps {
  pads: any[];
  isLoading?: boolean;
  error?: string | null;
}

export const Teams = ({ pads, isLoading, error }: TeamsProps) => {
  const acList = useSelector(getActiveCraft);
  const dispatch = useDispatch();
  const clients = useSelector(selectCellClients);
  const landingZones = useSelector(selectLandingZones);
  const padConfig = useSelector(selectPadConfig);
  const cellId = useSelector(selectSelectedCellId);
  const pointOfSales = useSelector(selectPointOfSales);
  const [padAircraftMap, setPadAircraftMap] = useState<{[key: number]: number}>();
  const [vendorSort, setVendorSort] = useState({ field: 'posName', direction: 'asc' });

  useEffect(() => {
    const padMap: {[key: number]: number} = {};
    pads.forEach(p => {
      padMap[p.id] = p.aircraft;
    });
    setPadAircraftMap(padMap);
  }, [pads]);

  // Helper function to get logo URL or fallback to icon
  const getLogoUrl = (pointOfSale: any) => {
    return pointOfSale.logo || null;
  };

  // Helper function to sort vendors
  const sortVendors = (vendors: any[], sortConfig: any, clients: any[]) => {
    return [...vendors].sort((a, b) => {
      // Find clients for both vendors
      const clientA = clients.find(client => {
        const stores = client.stores?.map((store: string) => parseInt(store));
        return stores?.includes(a?.id);
      });
      
      const clientB = clients.find(client => {
        const stores = client.stores?.map((store: string) => parseInt(store));
        return stores?.includes(b?.id);
      });

      if (sortConfig.field === 'deviceId') {
        const deviceIdA = clientA?.id?.slice(0, 4) || '';
        const deviceIdB = clientB?.id?.slice(0, 4) || '';
        return sortConfig.direction === 'asc' 
          ? deviceIdA.localeCompare(deviceIdB)
          : deviceIdB.localeCompare(deviceIdA);
      } else {
        // Sort by posName
        return sortConfig.direction === 'asc' 
          ? (a?.posName || '').localeCompare(b?.posName || '')
          : (b?.posName || '').localeCompare(a?.posName || '');
      }
    });
  };

  // Handle sort change
  const handleVendorSortChange = (field: string) => {
    setVendorSort(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  return (
    <div className="w-full h-[92%] mx-auto overflow-y-scroll p-5">
      <div className="py-1">
        {/* Pads Section */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-4">Pads</h2>
          <div className="overflow-x-auto">
            <div className="min-w-full rounded-lg border border-gray-200 overflow-hidden">
              <div className="divide-y divide-gray-200">
                {pads
                  .filter(pad => pad.cellId === cellId)
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((pad, index) => {
                    const recordCraft = (evt: any) => {
                      dispatch(linkPadWithAircraft(pad.id, evt.target.value));
                    };
                    const recordChannel = (evt: any) => {
                      dispatch(linkPadWithChannel(pad.id, evt.target.value));
                    };
                    const updatePad = (newVal: boolean) => {
                      dispatch(updateLandingPadActive(pad.id, newVal));
                    };
                    const config = padConfig?.[pad?.id] || {};

                    const rmAircraftFromPad = () => {
                      dispatch(
                        removeAircraftFromPad(pad?.id, padAircraftMap?.[pad?.id])
                      );
                    };

                    const getAircraftListForPad = (padId: number) => {
                      const selectedAircraft = padAircraftMap?.[padId];
                      const listOfAircraft = acList?.map(ac => {
                        return {
                          id: ac,
                          displayName: `${ac}`
                        };
                      });

                      if (
                        selectedAircraft !== 0 &&
                        !acList.includes(`${selectedAircraft}`)
                      ) {
                        listOfAircraft.push({
                          id: selectedAircraft,
                          displayName: `${selectedAircraft} (offline)`
                        });
                      }
                      return listOfAircraft.map(ac => {
                        return (
                          <MenuItem key={`AC-${ac.id}`} value={parseInt(ac.id.toString())}>
                            {'MNA-' + ac.displayName}
                          </MenuItem>
                        );
                      });
                    };

                    const getLandingZoneIdForPad = (padLandingZoneId: string) => {
                      let landingZone: any[] = [];
                      if (landingZones) {
                        landingZone = landingZones.filter(lz => {
                          if (lz.id === padLandingZoneId) {
                            return lz;
                          }
                        });
                      }
                      return landingZone[0]?.name;
                    };

                    return (
                      <div
                        key={`pads-${pad?.id}`}
                        className={`p-3 grid grid-cols-1 md:grid-cols-3 gap-4 items-center ${
                          index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                        } hover:bg-blue-50 transition-colors`}>
                        <div className="flex items-center col-span-1">
                          <span className="font-medium">{pad.name}</span>
                          {pad?.landingZoneId && (
                            <span className="ml-3 text-gray-600">
                              ({getLandingZoneIdForPad(pad.landingZoneId)})
                            </span>
                          )}
                        </div>
                        <div className="flex items-center flex-wrap gap-2 col-span-2">
                          <FormControl className="min-w-[150px]">
                            <FormControlLabel
                              label="Enable pad"
                              control={
                                <Switch
                                  checked={pad.active}
                                  onChange={(evt) => {
                                    updatePad(evt.target.checked);
                                  }}
                                  color="primary"
                                />
                              }
                            />
                          </FormControl>

                          {padAircraftMap &&
                          padAircraftMap.hasOwnProperty(pad?.id) ? (
                            <>
                              <FormControl style={{ minWidth: '120px' }}>
                                <InputLabel>AIRCRAFT</InputLabel>
                                <Select
                                  value={padAircraftMap[pad?.id]}
                                  onChange={recordCraft}
                                  label="AIRCRAFT">
                                  {getAircraftListForPad(pad?.id)}
                                </Select>
                              </FormControl>
                              <Button
                                onClick={rmAircraftFromPad}
                                variant="outlined"
                                color="error"
                                size="small"
                                className="mt-2 min-w-[150px]">
                                Remove
                              </Button>
                            </>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        </div>

        {/* Ground Teams Section */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-4">Ground Teams</h2>
          <div className="overflow-x-auto">
            <div className="min-w-full rounded-lg border border-gray-200 overflow-hidden">
              <div className="bg-gray-50 border-b border-gray-200">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell className="font-semibold text-sm text-gray-700">
                        User
                      </TableCell>
                      <TableCell className="font-semibold text-sm text-gray-700">
                        Checked In
                      </TableCell>
                      <TableCell className="font-semibold text-sm text-gray-700">
                        Version
                      </TableCell>
                      <TableCell className="font-semibold text-sm text-gray-700">
                        Latency
                      </TableCell>
                      <TableCell className="font-semibold text-sm text-gray-700">
                        App Type
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {clients
                      ?.filter(
                        client =>
                          client.type === 'GroundTeamApp' ||
                          client.type === 'CollectorApp'
                      )
                      .map((client, index) => {

                        const getCheckin = () => {
                          if (!client?.checkIn) return 'No check in';
                          const checkIn = new Date(client?.checkIn);
                          const lastCheckInSeconds =
                            (Date.now() - checkIn.getTime()) / 1000;
                          if (lastCheckInSeconds < 60) {
                            return `Now`;
                          }
                          if (lastCheckInSeconds > 60 * 60) {
                            return `${(lastCheckInSeconds / 60 / 60).toFixed(
                              0
                            )} hours ago`;
                          }
                          if (lastCheckInSeconds > 60) {
                            return `${(lastCheckInSeconds / 60).toFixed(
                              0
                            )} mins ago`;
                          }
                        };
                        if (!client?.checkIn) return null;

                        return (
                          <TableRow
                            key={client.id}
                            className={`${
                              index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                            } hover:bg-blue-50 transition-colors`}>
                            <TableCell>{client?.username}</TableCell>
                            <TableCell>{getCheckin()}</TableCell>
                            <TableCell>{client?.version}</TableCell>
                            <TableCell>{client?.latency}ms</TableCell>
                            <TableCell>
                              <Chip
                                label={client.type}
                                color={
                                  client.type === 'CollectorApp'
                                    ? 'warning'
                                    : 'primary'
                                }
                                variant="outlined"
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        </div>

        {/* Vendors Section */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-4">Vendors</h2>
          <div className="overflow-x-auto">
            <div className="min-w-full rounded-lg border border-gray-200 overflow-hidden">
              <div className="bg-gray-50 border-b border-gray-200">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell className="font-semibold text-sm text-gray-700">
                        <TableSortLabel
                          active={vendorSort.field === 'posName'}
                          direction={vendorSort.field === 'posName' ? vendorSort.direction as any : 'asc'}
                          onClick={() => handleVendorSortChange('posName')}>
                          Vendor
                        </TableSortLabel>
                      </TableCell>
                      <TableCell className="font-semibold text-sm text-gray-700">
                        Checked In
                      </TableCell>
                      <TableCell className="font-semibold text-sm text-gray-700">
                        <TableSortLabel
                          active={vendorSort.field === 'deviceId'}
                          direction={vendorSort.field === 'deviceId' ? vendorSort.direction as any : 'asc'}
                          onClick={() => handleVendorSortChange('deviceId')}>
                          Device ID
                        </TableSortLabel>
                      </TableCell>
                      <TableCell className="font-semibold text-sm text-gray-700">
                        Version
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortVendors(
                      pointOfSales
                        .filter(pointOfSale => {
                          const storeIds = clients
                            .map(client => {
                              const stores = client.stores?.map((store: string) =>
                                parseInt(store)
                              );
                              return stores;
                            })
                            ?.flat()
                            ?.includes(pointOfSale?.id);
                          return storeIds;
                        }),
                      vendorSort,
                      clients
                    )
                      .map((pointOfSale, index) => {
                        const client = clients.find(client => {
                          const stores = client.stores?.map((store: string) =>
                            parseInt(store)
                          );
                          return stores?.includes(pointOfSale?.id);
                        });

                        const getCheckin = () => {
                          if (!client?.checkIn) return 'No check in';
                          const checkIn = new Date(client?.checkIn);
                          const lastCheckInSeconds =
                            (Date.now() - checkIn.getTime()) / 1000;
                          if (lastCheckInSeconds < 60) {
                            return `Now`;
                          }
                          if (lastCheckInSeconds > 60 * 60) {
                            return `${(lastCheckInSeconds / 60 / 60).toFixed(
                              0
                            )} hours ago`;
                          }
                          if (lastCheckInSeconds > 60) {
                            return `${(lastCheckInSeconds / 60).toFixed(
                              0
                            )} mins ago`;
                          }
                        };

                        if (!client?.id) return null;

                        return (
                          <TableRow
                            key={`Device-${pointOfSale.id}-${client.id?.slice(0, 4)}`}
                            className={`${
                              index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                            } hover:bg-blue-50 transition-colors`}>
                            <TableCell>
                              <div className="flex items-center">
                                {getLogoUrl(pointOfSale) ? (
                                  <img
                                    src={getLogoUrl(pointOfSale)}
                                    alt={`${pointOfSale.posName} logo`}
                                    className="w-10 h-10 rounded-full object-cover mr-3 border border-gray-200"
                                  />
                                ) : (
                                  <StorefrontIcon className="w-10 h-10 rounded-full mr-3 bg-gray-200 p-2 text-gray-600" />
                                )}
                                <span>{pointOfSale?.posName}</span>
                              </div>
                            </TableCell>
                            <TableCell>{getCheckin()}</TableCell>
                            <TableCell>
                              Device-{client.id?.slice(0, 4)}
                            </TableCell>
                            <TableCell>
                              {client?.version || 'Unknown'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 